import { axiosBaseQuery } from "@king-ict/eupisi-ui/utils";
import { createApi } from "@reduxjs/toolkit/query/react";

import ChildRequestsCount from "../models/Request/ChildRequestsCount";
import CreateUpdateRequest from "../models/Request/CreateUpdateRequest";
import CreateUpdateRequestBenefit from "../models/Request/CreateUpdateRequestBenefit";
import Request from "../models/Request/Request";
import RequestListItem from "../models/Request/RequestListItem";
import UpdateRequestsPriority from "../models/Request/UpdateRequestPriority";

interface UpdateRequestParams {
  request: CreateUpdateRequest;
  id: string;
}

interface CreateDraftRequestParams {
  request: Request;
  requestId?: string;
}

interface UpdateDraftRequestParams {
  request: Request;
  guid: string;
}

interface ApplyRequestBenefitsParams {
  requestBenefitsDto: CreateUpdateRequestBenefit;
  id: string;
}

const mapRequestListItems = (requests: RequestListItem[]) =>
  requests.map((request) => ({
    ...request,
    pedagogicalYear: {
      ...request.pedagogicalYear,
      dateFrom: new Date(request.pedagogicalYear.dateFrom),
      dateTo: new Date(request.pedagogicalYear.dateTo),
    },
  }));

export const requestApi = createApi({
  reducerPath: "requestApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Requests"],
  endpoints: (build) => ({
    getRequests: build.query<RequestListItem[], void>({
      query: () => "requests",
      transformResponse: (response: RequestListItem[]) =>
        mapRequestListItems(response),
      providesTags: ["Requests"],
    }),
    getRequestCountsPerChild: build.query<ChildRequestsCount[], void>({
      query: () => "requests/count-per-child",
    }),
    getRequestCountByChildOib: build.query<
      ChildRequestsCount,
      { childOib: string; applicantParentOib?: string }
    >({
      query: ({ childOib, applicantParentOib }) => ({
        url: "requests/count-by-childoib",
        data: { childOib, applicantParentOib },
      }),
    }),
    createRequest: build.mutation<string, CreateUpdateRequest>({
      query: (request) => ({
        url: "requests",
        method: "post",
        data: request,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Requests"]),
    }),
    updateRequest: build.mutation<string, UpdateRequestParams>({
      query: ({ request, id }) => ({
        url: `requests/${id}`,
        method: "put",
        data: request,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Requests"]),
    }),
    createDraftRequest: build.mutation<unknown, CreateDraftRequestParams>({
      query: ({ request, requestId }) => ({
        url: `requests/drafts/${requestId ?? ""}`,
        method: "post",
        data: request,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Requests"]),
    }),
    updateDraftRequest: build.mutation<unknown, UpdateDraftRequestParams>({
      query: ({ request, guid }) => ({
        url: `requests/drafts/${guid}`,
        method: "put",
        data: request,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Requests"]),
    }),
    deleteRequest: build.mutation<unknown, number>({
      query: (requestId) => ({
        url: `requests/${requestId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Requests"]),
    }),
    deleteDraftRequest: build.mutation<unknown, string>({
      query: (guid) => ({
        url: `requests/drafts/${guid}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Requests"]),
    }),
    sendPhysicalExaminationDocumentation: build.mutation<unknown, FormData>({
      query: (formData) => ({
        url: "requests/physical-examination",
        method: "post",
        data: formData,
      }),
    }),
    applyRequestBenefits: build.mutation<unknown, ApplyRequestBenefitsParams>({
      query: ({ requestBenefitsDto, id }) => ({
        url: `requests/benefits/${id}`,
        method: "post",
        data: requestBenefitsDto,
      }),
    }),
    updateRequestPriorities: build.mutation<void, UpdateRequestsPriority[]>({
      query: (requests) => ({
        url: "requests/priorities",
        method: "put",
        data: requests,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["Requests"]),
      async onQueryStarted(
        updatedRequestPriorities,
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          requestApi.util.updateQueryData(
            "getRequests",
            undefined,
            (draftRequests) => {
              updatedRequestPriorities.forEach((updatedRequestPriority) => {
                const existingRequest = draftRequests.find(
                  (request) => request.id === updatedRequestPriority.requestId
                );

                if (existingRequest) {
                  existingRequest.priority = updatedRequestPriority.priority;
                }
              });
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetRequestsQuery,
  useGetRequestCountsPerChildQuery,
  useGetRequestCountByChildOibQuery,
  useCreateRequestMutation,
  useUpdateRequestMutation,
  useCreateDraftRequestMutation,
  useUpdateDraftRequestMutation,
  useDeleteRequestMutation,
  useDeleteDraftRequestMutation,
  useSendPhysicalExaminationDocumentationMutation,
  useApplyRequestBenefitsMutation,
  useUpdateRequestPrioritiesMutation,
} = requestApi;
